import { formatReceivedTime, getOrderTiming, parseReceivedTime } from "../../../../KDS/orderTiming";
import { formatPrepSeconds, getItemPreparationEstimate } from "../../../../KDS/itemPreparation";
import { formatKitchenDelay } from "../../../../table-layout/tableServingTiming";

const id = (value) => value == null ? "" : String(value).trim();
const sequenceOf = ({ task, job }) => {
  if (task?.metadata?.placedSequence != null) return task.metadata.placedSequence;
  const known = [...new Set((job.tasks || []).map((item) => item?.metadata?.placedSequence)
    .filter((value) => value != null).map(id))];
  return known.length === 1 ? known[0] : known.length === 0 ? job.orderSequence ?? null : null;
};
const sameSequence = (item, record) => sequenceOf(record) == null ||
  id(item.placedSequence) === id(sequenceOf(record));

/** Resolve actual cart lines, never a dish name shared by different KOTs. */
export function buildKotKitchenMatches(groups = [], jobs = [], cartId) {
  const products = groups.flatMap((group) => (group.items || []).map((item) => ({
    ...item, placedSequence: item.placedSequence ?? group.sequence,
  }))).filter((item) => item.productStatus !== "RETURNED");
  const records = jobs.filter((job) => id(cartId) && id(job.metadata?.cartId) === id(cartId))
    .flatMap((job) => (Array.isArray(job.tasks) ? job.tasks : []).filter(Boolean).map((task) => ({ job, task })));
  const matches = new Map();
  records.forEach((record) => {
    const { job, task } = record;
    const explicitId = id(task.metadata?.cartProductId);
    const candidates = (explicitId ? [explicitId] : [task.taskId, task.itemId, task.externalId]).map(id).filter(Boolean);
    let exact = products.filter((item) => id(item.cartProductId) &&
      candidates.includes(id(item.cartProductId)) && sameSequence(item, record));
    if (!explicitId && !exact.length && id(task.externalId)) {
      // Older KOTs use GTINs. A sequence can disambiguate repeated dishes, but
      // multiple variants/add-ons or multiple tasks still require exact IDs.
      const peers = records.filter((candidate) =>
        id(candidate.task.externalId) === id(task.externalId) &&
        (sequenceOf(record) == null || sequenceOf(candidate) == null ||
          id(sequenceOf(candidate)) === id(sequenceOf(record))));
      const productsByGtin = products.filter((item) =>
        id(item.gtin) === id(task.externalId) && sameSequence(item, record));
      if (peers.length === 1 && productsByGtin.length === 1) exact = productsByGtin;
    }
    if (exact.length !== 1) return;
    const key = id(exact[0].cartProductId);
    if (!matches.has(key)) matches.set(key, []);
    const entries = matches.get(key);
    if (!entries.some((entry) => id(entry.job.jobId) === id(job.jobId) &&
      entry.task === task)) entries.push(record);
  });
  return matches;
}

const taskState = ({ task, job }) => {
  const codes = [task.taskStatus, task.status].map((value) => id(value).toUpperCase());
  if (codes.includes("REMOVED")) return "removed";
  if (codes.some((code) => ["REJECTED", "CANCELLED", "CANCELED"].includes(code))) return "rejected";
  if (job.status === "REJECTED") return "rejected";
  if (codes.some((code) => ["COMPLETED", "READY"].includes(code))) return "ready";
  if (job.status === "COMPLETED") return "ready";
  if (codes.some((code) => ["IN_PROGRESS", "ACCEPTED", "PREPARING"].includes(code))) return "preparing";
  if (codes.includes("PENDING")) return "pending";
  if (job.status === "IN_PROGRESS") return "preparing";
  if (job.status === "PENDING") return "pending";
  return "unknown";
};

const clockDuration = (milliseconds) => {
  const seconds = Math.floor(Math.max(0, milliseconds) / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

const getKotPreparationEstimate = (item, task = {}, job = {}) => {
  const kitchen = getItemPreparationEstimate({}, task);
  if (kitchen.estimateSource === "item") return kitchen;
  const cart = getItemPreparationEstimate({}, {
    ...item,
    prepTime: item?.prepTime ?? item?.metadata?.prepTime ??
      item?.metadata?.avgPrepTime ?? item?.avgPrepTime,
  });
  if (cart.estimateSource === "item") return cart;
  return getItemPreparationEstimate({
    prepTime: job?.metadata?.avgPrepTime ?? job?.prepTime,
  }, {});
};

export function getKotItemKitchenTiming(item, matches = [], now = Date.now(), loadStatus = "ready") {
  if (item?.placedSequence != null) matches = matches.filter((record) => sameSequence(item, record));
  const states = matches.map(taskState);
  const statusKey = item?.productStatus === "RETURNED" ? "recalled"
    : loadStatus === "loading" ? "loading"
    : loadStatus !== "ready" || !states.length ? "unknown"
    : states.includes("preparing") ? "preparing"
    : states.includes("pending") ? "pending"
    : states.every((state) => state === "ready") ? "ready"
    : states.includes("ready") && states.some((state) => ["rejected", "removed"].includes(state)) ? "partial"
    : states.includes("unknown") ? "unknown"
    : states.includes("rejected") ? "rejected" : "removed";
  const statusLabel = {
    pending: "Yet to be accepted", preparing: "Preparing", ready: "Ready",
    rejected: "Rejected", removed: "Removed", recalled: "Recalled",
    partial: "Partly ready",
    unknown: "Kitchen status unavailable", loading: "Checking kitchen status",
  }[statusKey];
  const live = matches.filter((record) => ["preparing", "pending"].includes(taskState(record)));
  const sources = live.length ? live : matches;
  const options = sources.map(({ task, job }) => {
    const preparation = getKotPreparationEstimate(item, task, job);
    const prepMinutes = preparation.prepSeconds == null ? null : preparation.prepSeconds / 60;
    const ordered = parseReceivedTime(item?.placedTime) ?? parseReceivedTime(task.metadata?.placedTime) ?? parseReceivedTime(job.created);
    const timing = getOrderTiming({ created: ordered, prepTime: prepMinutes }, now);
    return { ...preparation, prepMinutes, ordered, timing,
      remaining: ordered == null || prepMinutes == null ? Infinity : ordered + prepMinutes * 60000 - now };
  }).sort((a, b) => a.remaining - b.remaining);
  const selected = options[0];
  const ordered = selected?.ordered ?? parseReceivedTime(item?.placedTime);
  const preparation = selected || getKotPreparationEstimate(item);
  const prepMinutes = preparation.prepSeconds == null ? null : preparation.prepSeconds / 60;
  const result = {
    statusKey, statusLabel, orderedText: formatReceivedTime(ordered),
    prepText: formatPrepSeconds(preparation.prepSeconds),
    prepSource: prepMinutes == null ? "Unavailable" : preparation.estimateSource === "order"
      ? "KOT average" : "Item estimate",
    progressPercent: null, tone: "neutral", timeText: "", countdownLabel: "", countdownDisplay: "",
    progressLabel: "", description: "Kitchen status is read from the matched task, not inferred from elapsed time.",
  };
  if (statusKey === "ready") return {
    ...result, progressPercent: 100, tone: "green", timeText: "Confirmed ready by kitchen",
    progressLabel: "Ready", description: "Readiness is confirmed by the kitchen; this is not an estimated completion time.",
  };
  // A missing kitchen match must not hide an otherwise valid KOT deadline.
  // Keep readiness unknown; elapsed time is never evidence of completion.
  if (!["pending", "preparing", "unknown", "loading"].includes(statusKey)) return result;
  if (ordered == null || prepMinutes == null || !Number.isFinite(now)) return {
    ...result, timeText: "Timer unavailable",
  };
  const timing = getOrderTiming({ created: ordered, prepTime: prepMinutes }, now);
  const elapsed = Math.max(0, now - ordered);
  const countdownLabel = timing.label === "Overdue by" ? "Overdue" : timing.label;
  return {
    ...result, progressPercent: Math.min(100, elapsed / (prepMinutes * 60000) * 100),
    tone: timing.tone,
    timeText: `${countdownLabel} ${timing.display}`,
    countdownLabel, countdownDisplay: timing.display,
    progressLabel: `Prep window elapsed · ${formatKitchenDelay(clockDuration(elapsed))}`,
    description: ["unknown", "loading"].includes(statusKey)
      ? `${loadStatus === "loading" ? "Kitchen status is loading."
        : loadStatus !== "ready" ? "Kitchen status could not be loaded."
        : matches.length ? "The matched kitchen task has no recognised status."
        : "No kitchen task could be safely linked to this KOT item."} The countdown uses the KOT order time and supplied prep estimate, not confirmed cooking progress.`
      : "Elapsed time since ordering includes waiting for acceptance. It is not cooking-completion progress, and never automatically marks an item ready.",
  };
}

/** Bound historical/ready-job reads to this cart's KOT dates, including midnight. */
export function getKotKitchenDateRange(groups = [], now = Date.now()) {
  const dates = groups.flatMap((group) => group.items || [])
    .map((item) => parseReceivedTime(item.placedTime)).filter((value) => value != null);
  if (!dates.length) return {};
  const start = new Date(Math.min(...dates) - 60000);
  start.setUTCHours(0, 0, 0, 0);
  const end = new Date(Math.max(now, ...dates));
  end.setUTCHours(23, 59, 59, 999);
  return { createdStart: start.toISOString(), createdEnd: end.toISOString() };
}
